import { goToUrl } from "apps/visits/core/utils/go-to-url";
import { Badge, Card } from "apps/visits/packages/ui/src/components";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Button } from "@/core/shared/v2/ui/button";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import { safeTrackEvent, selectPromoVoucherEvent } from "../../tracking/events";
import type { ConfigName } from "../../types/config-name";

type VoucherConfigCardProps = {
  voucherConfig: {
    image: string;
    title: string;
    description: string;
    path: string;
    buttonLabel: string;
    limitReached?: boolean;
    configName: ConfigName;
  };
};

export const VoucherConfigCard = ({ voucherConfig }: VoucherConfigCardProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { voucherDistribution } = useVouchersDistributionState();

  const handleOnClick = () => {
    safeTrackEvent(
      () => selectPromoVoucherEvent({ promotionType: voucherConfig.configName }),
      "select_promo_voucher",
      "voucher-config-card handleOnClick",
    );

    // Consultations live outside the unified SPA (separate Rails route) → full page navigation
    if (voucherConfig.configName === "consultations") {
      goToUrl(voucherConfig.path);
      return;
    }

    // Other flows (free_packages / purchase_products / friends_and_family) are part of the
    // unified SPA → push relative to the BrowserRouter basename to keep the single GTM session.
    const basenamePrefix = voucherDistribution ? `/pp/${voucherDistribution.slug}` : "";
    const relativePath = voucherConfig.path.replace(basenamePrefix, "") || "/";
    history.push(relativePath);
  };

  return (
    <Card className="sm:card-side w-full max-w-[400px] bg-white text-black shadow-xl sm:max-w-full">
      <Card.Image src={voucherConfig.image} className="h-[250px] w-full sm:h-full sm:w-80" />
      <Card.Body className="flex-1 items-center text-center sm:items-start sm:text-left">
        {voucherConfig.limitReached && (
          <Badge variant="neutral" className="mb-2">
            {t("VOUCHERS_DISTRIBUTION.ERRORS.DISTRIBUTION_CONFIG_LIMIT_EXCEEDED")}
          </Badge>
        )}
        <Card.Title tag="h2" className="">
          {voucherConfig.title}
        </Card.Title>
        <p className="mt-1">{voucherConfig.description}</p>
        <Card.Actions className="mt-4">
          <Button size="lg" variant="primary" onClick={handleOnClick}>
            {voucherConfig.buttonLabel}
          </Button>
        </Card.Actions>
      </Card.Body>
    </Card>
  );
};
